// src/pages/admin-dashboard/index.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
// import QuickActionButton from "../../components/ui/QuickActionButton";
import MetricCard from "./components/MetricCard";
import AppointmentOverview from "./components/AppointmentOverview";
import AppointmentModal from "../appointment-calendar/components/AppointmentModal";
import BusinessHealthChart from "./components/BusinessHealthChart";
import RevenueByStaffList from "./components/RevenueByStaffList";
import ActivityFeed from "./components/ActivityFeed";
import BusinessSelector from "./components/BusinessSelector";

import api, { getAppointments } from "../../lib/api";
import { listClients } from "../../lib/clients";
import { listStaff } from "../../lib/staff";
import { listTimeOff } from "../../lib/timeOff";
import { useAuth } from "../../auth/AuthProvider";
import i18n from "../../i18n";

/* =========================
   Helperi generali
========================= */

const mapStatus = (apiStatus) => {
  switch (apiStatus) {
    case "booked":
      return "confirmed";
    case "cancelled":
      return "cancelled";
    case "completed":
      return "completed";
    default:
      return "pending";
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

const monthShortRo = (monthIndex) => {
  const roNames = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];
  const lang = (i18n.language || "ro").slice(0, 2);
  if (lang === "ro") return roNames[monthIndex] ?? "";
  try {
    return new Intl.DateTimeFormat(lang, { month: "short" })
      .format(new Date(2021, monthIndex, 1))
      .replace(/\.$/, "");
  } catch {
    return roNames[monthIndex] ?? "";
  }
};

const extractPrice = (row) => {
  const raw =
    row.total_price ??
    row.price ??
    row.service_price ??
    (row.service && row.service.price);
  const n = parseFloat(raw || 0);
  return Number.isFinite(n) ? n : 0;
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat("ro-RO", {
    style: "currency",
    currency: "RON",
  }).format(amount || 0);

const buildChange = (current, previous, suffix) => {
  const prev = Number(previous || 0);
  const cur = Number(current || 0);

  if (prev <= 0) {
    return { change: "—", changeType: "positive" };
  }

  const pct = ((cur - prev) / prev) * 100;
  const rounded = Math.round(pct);
  const sign = rounded > 0 ? "+" : "";
  const type = rounded < 0 ? "negative" : "positive";

  return { change: `${sign}${rounded}% ${suffix}`, changeType: type };
};


/* =========================
   Componentă principală
   ========================= */

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentBusiness, setCurrentBusiness] = useState(null);

  const { businesses, currentBusinessId, setCurrentBusinessId } = useAuth();

  // scope: owner poate vedea All sau o locație (setat din Header)
  const [locationScopeId, setLocationScopeId] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("current_location_scope_id") || "";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => {
      setLocationScopeId(localStorage.getItem("current_location_scope_id") || "");
    };
    sync();
    window.addEventListener("location-scope:changed", sync);
    return () => window.removeEventListener("location-scope:changed", sync);
  }, [currentBusinessId]);

  // programări pentru analytics (ultimul an)
  const [analyticsAppointments, setAnalyticsAppointments] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState("");

  // clienți
  const [totalClients, setTotalClients] = useState(0);
  const [newClientsThisMonth, setNewClientsThisMonth] = useState(0);
  const [newClientsPrevMonth, setNewClientsPrevMonth] = useState(0);
  // staff (personal)
  const [staffCount, setStaffCount] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [staffFilterId, setStaffFilterId] = useState("all");
  // concedii
  const [timeOffToday, setTimeOffToday] = useState([]);
  const [utilizationRange, setUtilizationRange] = useState("month"); // week | month | quarter | year

  // modal edit programare (din dashboard)
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // ==============================
  // Fetch appointments (ultimul an)
  // ==============================
  useEffect(() => {
    if (!currentBusinessId) {
      setAnalyticsAppointments([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setAnalyticsLoading(true);
      setAnalyticsError("");

      try {
        // vrem ultimul an, dar până A DOUA zi, ca să includem toată ziua de azi
        const now = new Date();

        // to = mâine la 00:00 (aprox) – include toate programările de azi
        const to = new Date(now);
        to.setDate(to.getDate() + 1);

        // from = cu 1 an în urmă față de "to"
        const from = new Date(to);
        from.setUTCFullYear(to.getUTCFullYear() - 1); // ultimul an

        const rows = await getAppointments({
          from: from.toISOString(),
          to: to.toISOString(),
          locationId: locationScopeId || undefined,
        });

        if (cancelled) return;
        setAnalyticsAppointments(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (cancelled) return;
        console.error("Eroare la încărcarea programărilor pentru dashboard:", err);
        setAnalyticsAppointments([]);
        setAnalyticsError(
          err?.response?.data?.detail ||
          err?.message ||
          "Eroare la încărcarea programărilor"
        );
      } finally {
        !cancelled && setAnalyticsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentBusinessId, locationScopeId]);

  // Util pentru când editezi o programare direct din dashboard
  const reloadAnalyticsAppointments = async () => {
    if (!currentBusinessId) return;
    setAnalyticsLoading(true);
    setAnalyticsError("");

    try {
      // vrem ultimul an, dar până A DOUA zi, ca să includem toată ziua de azi
      const now = new Date();

      const to = new Date(now);
      to.setDate(to.getDate() + 1);

      const from = new Date(to);
      from.setUTCFullYear(to.getUTCFullYear() - 1);

      const rows = await getAppointments({
        from: from.toISOString(),
        to: to.toISOString(),
        locationId: locationScopeId || undefined,
      });

      setAnalyticsAppointments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Eroare la reîncărcarea programărilor:", err);
      setAnalyticsAppointments([]);
      setAnalyticsError(
        err?.response?.data?.detail || err?.message || "Eroare la încărcarea programărilor"
      );
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // ==============================
  // Fetch clienți
  // ==============================
  useEffect(() => {
    let cancelled = false;
    if (!currentBusinessId) {
      setTotalClients(0);
      setNewClientsThisMonth(0);
      return;
    }

    const loadClients = async () => {
      try {
        const data = await listClients({ businessId: currentBusinessId });
        if (cancelled) return;

        const arr = Array.isArray(data) ? data : [];
        setTotalClients(arr.length);

        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

        const countNew = arr.filter((c) => {
          const rawCreated = c.createdAt || c._raw?.created_at || null;


          if (!rawCreated) return false;

          const d = new Date(rawCreated);
          return d >= monthStart && d < nextMonth;
        }).length;


        const countPrev = arr.filter((c) => {
          const rawCreated = c.createdAt || c._raw?.created_at || null;


          if (!rawCreated) return false;

          const d = new Date(rawCreated);
          return d >= prevMonthStart && d < monthStart;
        }).length;

        setNewClientsThisMonth(countNew);
        setNewClientsPrevMonth(countPrev);

      } catch (err) {
        console.error("Eroare la încărcarea clienților pentru dashboard:", err);
        if (!cancelled) {
          setTotalClients(0);
          setNewClientsThisMonth(0);
        }
      }
    };

    loadClients();
    return () => {
      cancelled = true;
    };
  }, [currentBusinessId, locationScopeId]);

  // ==============================
  // Fetch staff (pentru utilizare)
  // ==============================
  useEffect(() => {
    let cancelled = false;
    if (!currentBusinessId) {
      setStaffCount(0);
      setStaffList([]);
      setStaffFilterId("all");
      return;
    }

    const loadStaff = async () => {
      try {
        const data = await listStaff();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setStaffList(list);
        setStaffCount(list.length);
      } catch (err) {
        console.error("Eroare la încărcarea staff-ului pentru utilizare:", err);
        if (!cancelled) {
          setStaffCount(0);
          setStaffList([]);
        }
      }
    };

    loadStaff();
    return () => {
      cancelled = true;
    };
  }, [currentBusinessId, locationScopeId]);

  // ==============================
  // Fetch concedii (pentru azi)
  // ==============================
  useEffect(() => {
    let cancelled = false;
    if (!currentBusinessId) {
      setTimeOffToday([]);
      return;
    }

    const loadTimeOff = async () => {
      try {
        const today = new Date();
        const todayStr = today.toISOString().slice(0, 10);
        const list = await listTimeOff({ status: "approved", from: todayStr, to: todayStr });
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        const t0 = new Date(); t0.setHours(0, 0, 0, 0);
        const onLeave = arr.filter((r) => {
          const s = new Date(r.start_at || r.start_date);
          const e = new Date(r.end_at || r.end_date);
          s.setHours(0, 0, 0, 0);
          e.setHours(23, 59, 59, 999);
          return r.status === "approved" && t0 >= s && t0 <= e;
        }).map((r) => ({
          staffId: String(r.staff_id || r.staff_user || ""),
          staffName: r.staff_name || "",
          type: r.type || "vacation",
        }));
        setTimeOffToday(onLeave);
      } catch {
        if (!cancelled) setTimeOffToday([]);
      }
    };

    loadTimeOff();
    return () => { cancelled = true; };
  }, [currentBusinessId]);

  // ==============================
  // Normalizare programări
  // ==============================
  const normalizedAppointments = useMemo(() => {
    return (analyticsAppointments || []).map((r) => {
      const start = new Date(r.start_at);
      const end = r.end_at ? new Date(r.end_at) : new Date(start.getTime());
      const durationMinutes = Math.max(
        0,
        Math.round((end - start) / 60000) || 0
      );
      const dateKey = start.toISOString().slice(0, 10); // YYYY-MM-DD
      const monthKey = `${start.getUTCFullYear()}-${pad2(
        start.getUTCMonth() + 1
      )}`;

      return {
        id: r.id,
        start,
        end,
        dateKey,
        monthKey,
        status: r.status,
        staffId: r.staff_id ?? null,
        staffName: r.staff_name || r.staff || "",
        serviceName: r.service_name || r.service?.name || "",
        clientName: r.client_name || "",
        durationMinutes,
        price: extractPrice(r),
        raw: r,
      };
    });
  }, [analyticsAppointments]);

  // ==============================
  // Today appointments & count
  // ==============================
  const todayAppointments = useMemo(() => {
    // startul zilei de azi (local)
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    // startul zilei de mâine (local)
    const end = new Date(start);
    end.setDate(end.getDate() + 1);

    return normalizedAppointments
      .filter((a) => a.start >= start && a.start < end)
      .map((a) => {
        const startTime = a.start.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        const endTime = a.end.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

        return {
          id: a.id,
          clientName: a.clientName,
          serviceName: a.serviceName,
          staffName: a.staffName,
          staffId: a.staffId,
          time: `${startTime} - ${endTime}`,
          duration: a.durationMinutes,
          status: mapStatus(a.status),
          _raw: a.raw,
        };
      });
  }, [normalizedAppointments]);

  const todayCount = useMemo(
    () => todayAppointments.filter((a) => a.status !== "cancelled").length,
    [todayAppointments]
  );

  const yesterdayCount = useMemo(() => {
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);

    const startYesterday = new Date(startToday);
    startYesterday.setDate(startYesterday.getDate() - 1);

    return normalizedAppointments.filter((a) => {
      return a.start >= startYesterday && a.start < startToday && a.status !== "cancelled";
    }).length;
  }, [normalizedAppointments]);

  const lastMonthRevenue = useMemo(() => {
    const now = new Date();
    const prev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const lastMonthKey = `${prev.getUTCFullYear()}-${pad2(prev.getUTCMonth() + 1)}`;

    return normalizedAppointments
      .filter((a) => a.monthKey === lastMonthKey && a.status === "completed")
      .reduce((sum, a) => sum + a.price, 0);
  }, [normalizedAppointments]);

  const prevUtilizationPercent = useMemo(() => {
    const now = new Date();
    const rangeDaysMap = { week: 7, month: 30, quarter: 90, year: 365 };
    const rangeDays = rangeDaysMap[utilizationRange] || 30;

    const utilStart = new Date(now);
    utilStart.setDate(utilStart.getDate() - rangeDays);

    const prevEnd = new Date(utilStart);
    const prevStart = new Date(utilStart);
    prevStart.setDate(prevStart.getDate() - rangeDays);

    const prevAppts = normalizedAppointments.filter(
      (a) => a.start >= prevStart && a.start < prevEnd && a.status !== "cancelled"
    );

    const bookedPrevMinutes = prevAppts.reduce((sum, a) => sum + a.durationMinutes, 0);
    const capacityMinutes = staffCount > 0 ? staffCount * rangeDays * 8 * 60 : 0;

    return capacityMinutes ? (bookedPrevMinutes / capacityMinutes) * 100 : 0;
  }, [normalizedAppointments, staffCount, staffList, utilizationRange]);


  // ==============================
  // Filtru personal (doar pentru zona de programări)
  // ==============================
  const onLeaveIds = useMemo(
    () => new Set((timeOffToday || []).map((r) => r.staffId)),
    [timeOffToday]
  );

  const staffOptions = useMemo(() => {
    const base = [{ label: t('dashboard.allStaffOption'), value: 'all' }];
    const extra = (staffList || []).map((s) => {
      const fullName = `${(s?.first_name || "").trim()} ${(s?.last_name || "").trim()}`.trim();
      const name = fullName || s?.name || s?.email || `#${s?.id}`;
      const onLeave = onLeaveIds.has(String(s?.id));
      return {
        label: onLeave ? `${name} ${t('dashboard.onLeaveSuffix')}` : name,
        value: String(s?.id),
      };
    });
    return [...base, ...extra];
  }, [staffList, onLeaveIds, t]);

  const filteredTodayAppointments = useMemo(() => {
    if (!staffFilterId || staffFilterId === 'all') return todayAppointments;
    return (todayAppointments || []).filter((a) => String(a?.staffId) === String(staffFilterId));
  }, [todayAppointments, staffFilterId]);

  // ==============================
  // Analytics derivate (venituri, grafice, statistici)
  // ==============================
  const analytics = useMemo(() => {
    if (!normalizedAppointments.length) {
      return {
        staffRevenueData: [],
        servicePopularityData: [],
        revenueTrendsData: [],
        quickStats: {
          showRate: 0,
          avgDuration: 0,
          recurringClientsPercent: 0,
          rating: null,
        },
        monthlyRevenue: 0,
        utilizationPercent: 0,
      };
    }

    const now = new Date();

    // ---- luna curentă pentru cardul "Venituri luna aceasta"
    const thisMonthKey = `${now.getUTCFullYear()}-${pad2(
      now.getUTCMonth() + 1
    )}`;
    const monthlyRevenue = normalizedAppointments
      .filter(
        (a) => a.monthKey === thisMonthKey && a.status === "completed"
      )
      .reduce((sum, a) => sum + a.price, 0);

    // ---- ultimele 30 de zile (pt staff revenue, popularitatea serviciilor, quick stats)
    const last30Start = new Date(now);
    last30Start.setDate(last30Start.getDate() - 30);
    const apptsLast30 = normalizedAppointments.filter(
      (a) => a.start >= last30Start
    );

    // Venituri încasate per angajat (ultimele 30 zile)
    const staffLabelById = new Map();
    (staffList || []).forEach((s) => {
      const id = s?.id != null ? String(s.id) : null;
      if (!id) return;
      const fullName = `${(s?.first_name || "").trim()} ${(s?.last_name || "").trim()}`.trim();
      const label = (fullName || s?.name || s?.email || `#${id}`).trim();
      staffLabelById.set(id, label);
    });

    const staffRevenueMap = new Map();
    // inițializăm cu staff-ul din business (ca să apară și cei cu 0)
    staffLabelById.forEach((label, id) => {
      staffRevenueMap.set(id, { staffKey: id, name: label, value: 0 });
    });

    apptsLast30.forEach((a) => {
      if (a.status !== "completed") return;

      const id = a.staffId != null ? String(a.staffId) : "unassigned";
      const name =
        staffLabelById.get(id) ||
        (a.staffName || "").trim() ||
        (id === "unassigned" ? "Neatribuit" : `#${id}`);

      const existing = staffRevenueMap.get(id) || { staffKey: id, name, value: 0 };
      existing.value += a.price;
      existing.name = name;
      staffRevenueMap.set(id, existing);
    });

    const staffRevenueRows = Array.from(staffRevenueMap.values()).sort(
      (a, b) => Number(b?.value || 0) - Number(a?.value || 0)
    );

    const staffRevenueTotal30d = staffRevenueRows.reduce(
      (sum, r) => sum + Number(r?.value || 0),
      0
    );

    // pentru grafic păstrăm Top 10 cu încasări
    const staffRevenueData = staffRevenueRows
      .filter((r) => Number(r?.value || 0) > 0)
      .slice(0, 10)
      .map(({ name, value }) => ({ name, value }));

    // Popularitatea serviciilor (număr programări / serviciu)
    const serviceCountMap = new Map();
    apptsLast30.forEach((a) => {
      if (a.status === "cancelled") return;
      const key = a.serviceName || "Serviciu";
      serviceCountMap.set(key, (serviceCountMap.get(key) || 0) + 1);
    });
    const servicePopularityData = Array.from(
      serviceCountMap.entries()
    ).map(([name, value]) => ({ name, value }));

    // Evoluția veniturilor (RON) – ultimele 6 luni
    const last6Start = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1)
    );
    const revenueByMonth = new Map();
    normalizedAppointments.forEach((a) => {
      if (a.status !== "completed" || a.start < last6Start) return;
      const y = a.start.getUTCFullYear();
      const m = a.start.getUTCMonth();
      const key = `${y}-${pad2(m + 1)}`;
      revenueByMonth.set(key, (revenueByMonth.get(key) || 0) + a.price);
    });

    const revenueTrendsData = [];
    const iter = new Date(last6Start);
    for (let i = 0; i < 6; i += 1) {
      const key = `${iter.getUTCFullYear()}-${pad2(iter.getUTCMonth() + 1)}`;
      revenueTrendsData.push({
        name: monthShortRo(iter.getUTCMonth()),
        value: revenueByMonth.get(key) || 0,
      });
      iter.setUTCMonth(iter.getUTCMonth() + 1);
    }

    // Statistici rapide – tot ultimele 30 de zile
    let completed = 0;
    let cancelled = 0;
    let totalDurationCompleted = 0;
    let completedCount = 0;
    const clientCounts = new Map();

    apptsLast30.forEach((a) => {
      if (a.status === "completed") {
        completed += 1;
        totalDurationCompleted += a.durationMinutes;
        completedCount += 1;
      } else if (a.status === "cancelled") {
        cancelled += 1;
      }

      const clientKey =
        (a.raw?.client_id ?? a.raw?.client ?? a.clientName) || null;

      if (clientKey) {
        clientCounts.set(clientKey, (clientCounts.get(clientKey) || 0) + 1);
      }
    });

    const showRateBase = completed + cancelled;
    const showRate = showRateBase ? (completed / showRateBase) * 100 : 100;
    const avgDuration = completedCount
      ? totalDurationCompleted / completedCount
      : 0;

    let recurringClients = 0;
    clientCounts.forEach((count) => {
      if (count >= 2) recurringClients += 1;
    });
    const recurringClientsPercent = clientCounts.size
      ? (recurringClients / clientCounts.size) * 100
      : 0;

    // Utilizare personal pe perioada selectată
    const rangeDaysMap = {
      week: 7,
      month: 30,
      quarter: 90,
      year: 365,
    };
    const rangeDays = rangeDaysMap[utilizationRange] || 30;
    const utilStart = new Date(now);
    utilStart.setDate(utilStart.getDate() - rangeDays);

    const utilAppts = normalizedAppointments.filter(
      (a) => a.start >= utilStart && a.status !== "cancelled"
    );
    const bookedMinutes = utilAppts.reduce(
      (sum, a) => sum + a.durationMinutes,
      0
    );
    const capacityMinutes =
      staffCount > 0 ? staffCount * rangeDays * 8 * 60 : 0;
    const utilizationPercent = capacityMinutes
      ? (bookedMinutes / capacityMinutes) * 100
      : 0;


    return {
      staffRevenueData,
      staffRevenueRows,
      staffRevenueTotal30d,
      servicePopularityData,
      revenueTrendsData,
      quickStats: {
        showRate,
        avgDuration,
        recurringClientsPercent,
        rating: null,
      },
      monthlyRevenue,
      utilizationPercent,
      bookedMinutes,
    };

  }, [normalizedAppointments, staffCount, staffList, utilizationRange]);

  const {
    staffRevenueData,
    staffRevenueRows,
    staffRevenueTotal30d,
    servicePopularityData,
    revenueTrendsData,
    quickStats,
    monthlyRevenue,
    utilizationPercent,
    bookedMinutes,
  } = analytics;


  // ==============================
  // Activitate recentă (din programări)
  // ==============================
  const recentActivities = useMemo(() => {
    const sorted = [...normalizedAppointments].sort(
      (a, b) => b.start - a.start
    );
    return sorted.slice(0, 10).map((a) => {
      let type = "appointment_created";
      if (a.status === "completed") type = "appointment_completed";
      else if (a.status === "cancelled") type = "appointment_cancelled";

      const statusText =
        a.status === "completed"
          ? t("dashboard.activityCompleted")
          : a.status === "cancelled"
            ? t("dashboard.activityCancelled")
            : t("dashboard.activityCreated");

      const user = a.staffName || t("dashboard.systemUser");
      // ActivityFeed afișează deja userul cu bold — descrierea nu-l mai repetă
      const description = `${statusText}${a.clientName ? ` ${t("dashboard.activityWith")} ${a.clientName}` : ""
        }`;

      // 👇 aici adăugăm încasarea, doar pentru programările finalizate
      const amountText =
        a.status === "completed" && a.price
          ? ` • ${formatCurrency(a.price)}`
          : "";

      const details = `${a.serviceName || t("calendar.service")} – ${a.start.toLocaleDateString(
        i18n.language || "ro"
      )} ${a.start.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}${amountText}`;

      return {
        id: a.id,
        type,
        user,
        description,
        details,
        timestamp: a.start,
      };
    });
  }, [normalizedAppointments, t, i18n.language]);


  // ==============================
  // Breadcrumb & business selector
  // ==============================
  const breadcrumbItems = [{ label: t("common.home"), path: "/app/admin-dashboard" }];

  useEffect(() => {
    if (businesses?.length) {
      const found =
        businesses.find((b) => String(b.id) === String(currentBusinessId)) ||
        businesses[0];
      setCurrentBusiness(found || null);
    } else {
      setCurrentBusiness(null);
    }
  }, [businesses, currentBusinessId]);

  const handleBusinessChange = (business) => {
    setCurrentBusiness(business);
    if (business?.id != null) {
      setCurrentBusinessId(business.id); // scrie și în localStorage
    }
  };

  // ==============================
  // Metric cards (unele cu date reale)
  // ==============================
  const utilizationRangeLabel = {
    week: t("dashboard.utilizationWeek"),
    month: t("dashboard.utilizationMonth"),
    quarter: t("dashboard.utilizationQuarter"),
    year: t("dashboard.utilizationYear"),
  }[utilizationRange];

  const utilizationDisplay = analyticsLoading
    ? "…"
    : bookedMinutes > 0 && utilizationPercent > 0 && utilizationPercent < 1
      ? "<1%"                                  // programări puține, dar există
      : `${Math.round(
        Math.max(0, Math.min(100, utilizationPercent))
      )}%`;

  const todayChange = buildChange(todayCount, yesterdayCount, t("dashboard.vsYesterday"));
  const revenueChange = buildChange(monthlyRevenue, lastMonthRevenue, t("dashboard.vsLastMonth"));
  const utilChange = buildChange(utilizationPercent, prevUtilizationPercent, t("dashboard.vsPreviousPeriod"));
  const clientsChange = buildChange(newClientsThisMonth, newClientsPrevMonth, t("dashboard.vsLastMonth"));

  const metrics = [
    {
      title: t("dashboard.appointmentsToday"),
      value: analyticsLoading ? "…" : String(todayCount || 0),
      change: todayChange.change,
      changeType: todayChange.changeType,
      icon: "Calendar",
      color: "primary",
    },
    {
      title: t("dashboard.revenueThisMonth"),
      value: analyticsLoading ? "…" : formatCurrency(monthlyRevenue),
      change: revenueChange.change,
      changeType: revenueChange.changeType,
      icon: "TrendingUp",
      color: "success",
    },
    {
      title: `${t("dashboard.staffUtilization")} ${utilizationRangeLabel || ""}`,
      value: utilizationDisplay,
      change: utilChange.change,
      changeType: utilChange.changeType,
      icon: "Users",
      color: "warning",
    },
    {
      title: t("dashboard.newClients"),
      value: String(newClientsThisMonth || 0),
      change: clientsChange.change,
      changeType: clientsChange.changeType,
      icon: "UserPlus",
      color: "primary",
    },

  ];

  // ==============================
  // Handlers navigație / acțiuni
  // ==============================
  const handleMetricClick = (metric) => {
    switch (metric?.icon) {
      case "Calendar":
        navigate("/app/appointment-calendar");
        break;
      case "Users":
        navigate("/app/staff-management");
        break;
      case "UserPlus":
        navigate("/app/client-management");
        break;
      default:
        break;
    }
  };

  const handleViewAllAppointments = () => {
    if (staffFilterId && staffFilterId !== "all") {
      navigate(`/app/appointment-calendar?staffId=${staffFilterId}`);
    } else {
      navigate("/app/appointment-calendar");
    }
  };

  const handleCreateNewAppointment = () => {
    navigate("/app/appointment-calendar?action=create");
  };

  // ==============================
  // Editare programări din dashboard
  const handleOpenAppointmentFromDashboard = (appointment) => {
    if (!appointment) return;
    setSelectedAppointment(appointment);
    setAppointmentModalOpen(true);
  };

  const handleCloseAppointmentModal = () => {
    setAppointmentModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleSaveAppointmentFromDashboard = async (payload) => {
    if (!payload?.id) return;
    await api.patch(`/b/appointments/${payload.id}`, payload);
    await reloadAnalyticsAppointments();
  };

  const handleDeleteAppointmentFromDashboard = async (id) => {
    if (!id) return;
    await api.delete(`/b/appointments/${id}`);
    await reloadAnalyticsAppointments();
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed((v) => !v);
  };

  // ==============================
  // Render
  // ==============================

  const todaySection = (
    <div className="mb-8">
      {analyticsError ? (
        <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700">
          {analyticsError}
        </div>
      ) : null}

      {timeOffToday.length > 0 && (
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 px-4 py-2.5 cursor-pointer hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
          onClick={() => navigate("/app/staff-management")}
          title="Mergi la managementul personalului"
        >
          <span className="text-yellow-700 dark:text-yellow-400 font-medium text-sm shrink-0">
            Absent{timeOffToday.length > 1 ? "i" : ""} azi:
          </span>
          {timeOffToday.map((r, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-full bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 text-xs px-2 py-0.5 font-medium"
            >
              {r.staffName || `#${r.staffId}`}
              {r.type === "sick" ? " · medical" : r.type === "personal" ? " · personal" : r.type === "day_off" ? " · zi liberă" : " · concediu"}
            </span>
          ))}
        </div>
      )}

      <AppointmentOverview
        appointments={filteredTodayAppointments}
        onViewAll={handleViewAllAppointments}
        onCreateNew={handleCreateNewAppointment}
        onAppointmentClick={handleOpenAppointmentFromDashboard}
        staffFilterId={staffFilterId}
        onStaffFilterChange={setStaffFilterId}
        staffOptions={staffOptions}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header
        isCollapsed={sidebarCollapsed}
        onToggleSidebar={handleSidebarToggle}
      />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />
      <main
        className={`transition-all duration-300 ease-out-custom page-top pb-20 lg:pb-6 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
          }`}
      >
        <div className="p-4 sm:p-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <Breadcrumbs items={breadcrumbItems} className="mb-2" />
              <h1 className="text-2xl font-bold text-foreground">
                {t("dashboard.adminTitle")}
              </h1>
              <p className="text-text-secondary mt-1">
                {t("dashboard.adminSubtitle")}
              </p>
            </div>

            {/* Business Selector */}
            <div className="mt-4 lg:mt-0">
              <BusinessSelector
                businesses={businesses || []}
                currentBusiness={currentBusiness}
                onBusinessChange={handleBusinessChange}
              />
            </div>
          </div>

          {/* Selector perioadă utilizare personal */}
          <div className="flex justify-end mb-4">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{t("dashboard.staffUtilizationPeriod")}:</span>
              <select
                value={utilizationRange}
                onChange={(e) => setUtilizationRange(e.target.value)}
                className="border border-border bg-background rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="week">{t("dashboard.periodWeek")}</option>
                <option value="month">{t("dashboard.periodMonth")}</option>
                <option value="quarter">{t("dashboard.periodQuarter")}</option>
                <option value="year">{t("dashboard.periodYear")}</option>
              </select>
            </label>
          </div>

          {/* Programări de astăzi + filtru personal (MOBILE: primul) */}
          <div className="lg:hidden">
            {todaySection}
          </div>


          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
            {metrics.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric.title}
                value={metric.value}
                change={metric.change}
                changeType={metric.changeType}
                icon={metric.icon}
                color={metric.color}
                onClick={() => handleMetricClick(metric)}
              />
            ))}
          </div>

          {/* Programări de astăzi + filtru personal (DESKTOP: după metrici) */}
          <div className="hidden lg:block">
            {todaySection}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            {/* 1. Venituri încasate per angajat (înlocuiește Tendințe rezervări săptămânale) */}
            <BusinessHealthChart
              data={staffRevenueData}
              type="bar"
              title={t("dashboard.chartRevenuePerEmployee")}
              valueFormatter={formatCurrency}
            />

            {/* 2. Popularitatea serviciilor (din baza de date) */}
            <BusinessHealthChart
              data={servicePopularityData}
              type="bar"
              title={t("dashboard.chartServicePopularity")}
            />

            {/* 3. Evoluția veniturilor (RON) – reală */}
            <BusinessHealthChart
              data={revenueTrendsData}
              type="line"
              title={t("dashboard.chartRevenueTrend")}
              valueFormatter={formatCurrency}
            />
          </div>

          <div className="mb-8">
            <RevenueByStaffList
              rows={staffRevenueRows}
              total={staffRevenueTotal30d}
              loading={analyticsLoading}
            />
          </div>

          {/* Activity Feed + Statistici rapide */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <ActivityFeed activities={recentActivities} />
            </div>

            <div className="bg-background border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-6">
                {t("dashboard.quickStatsTitle")}
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">
                    {t("dashboard.showRate")}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {quickStats
                      ? `${quickStats.showRate.toFixed(1)}%`
                      : "0%"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">
                    {t("dashboard.avgServiceTime")}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {quickStats
                      ? `${Math.round(quickStats.avgDuration)} min`
                      : "0 min"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">
                    {t("dashboard.recurringClients")}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {quickStats
                      ? `${quickStats.recurringClientsPercent.toFixed(1)}%`
                      : "0%"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">
                    {t("dashboard.avgRating")}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {quickStats?.rating != null ? "4,8/5" : "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">
                    {t("clients.stats.total")}
                  </span>
                  <span className="text-sm font-medium text-foreground">
                    {totalClients}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <AppointmentModal
          isOpen={appointmentModalOpen}
          appointment={selectedAppointment}
          onClose={handleCloseAppointmentModal}
          onSave={handleSaveAppointmentFromDashboard}
          onDelete={handleDeleteAppointmentFromDashboard}
        />
      </main>
      {/* <QuickActionButton /> */}
    </div>
  );
};

export default AdminDashboard;
